import { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { QueryReturnValue } from '@reduxjs/toolkit/query';
import {
  EmployeeByIdContributions,
  EmployeeSubmissionStatus,
  EmployeeType,
} from '@/common/common.types';
import { getBoardId, getUserPortalRole } from '@/core/web-storage/webStorage.utils';
import { baseAppApi } from '@/core/network/baseAppApi';
import { COMMON_HEADERS } from '@/core/network/network.constants';

import {
  AppStandardResponse,
  AppStandardResponseWithoutNesting,
  getFirstErrorOrDefault,
  getRangeFromHeader,
} from '@/core/network/network.utils';
import { getComputedBatchStatus } from '@/ertn/utils/ertnUtils';
import { ValidateUnsubmittedResponse } from '@/validation-service/services/validationApi';
import {
  transformValidateServiceResponse,
  excludeWarningsFromValidationErrors,
  isValidValidateUnsubmittedResponse,
} from '@/validation-service/services/validationApi.utils';
import tokens from '@/ertn/tokens.json';
import { PORTAL_ROLES } from '@/authorization/authorization.constants';

export const ERTN_PAGE_SIZE = 25;
export type GetERTNByYearItem = {
  ertnId: number;
  name: string;
  startYear: number;
  employerId: number;
  employeeCount: number;
  insertionTimestamp: string;
  transmissionDate: string;
  status: EmployeeSubmissionStatus;
  bypassCount: number;
};

export type GetErtnBatchResponse = {
  ertns: GetERTNByYearItem[];
  range: [number, number, number];
};

export type ERTNBatchStatusTransformed =
  | 'empty-batch'
  | 'batch-with-employee'
  | 'A'
  | 'E'
  | 'Unknown';
export type GetErtnByYearByStatusResponseTransformed = {
  ertns: (Omit<GetERTNByYearItem, 'status'> & {
    status: ERTNBatchStatusTransformed;
  })[];
  range: [number, number, number];
};

export type AddErtnBatchPayload = {
  ertnBatchName: string;
  schoolYear: number;
};
export type UpdateErtnBatchPayload = AddErtnBatchPayload & { ertnId: number };

export type GetErtnEmployees = Pick<
  EmployeeType,
  'employeeId' | 'lastName' | 'firstName' | 'sin' | 'terminationStatus' | 'terminationDate'
> & {
  contributions: (EmployeeByIdContributions & { hasError: boolean })[];
  bypassIndicator: boolean;
};

export type GetErtnEmployeesTransformed = Omit<GetErtnEmployees, 'contributions'> & {
  records: number;
  hasErrors: boolean;
};

export type CopyEmployeeRequest = {
  employeeId: number | undefined;
  ertnId: number | null;
  employeeIdsToValidate: number[];
};

export type BatchHistoryResponseItem = {
  employerId: number;
  employmentMonth: string;
  ertnId: number;
  batchId: number;
  insertionTimestamp: string;
  sin: string;
  status: string;
  validationBypassStatus: 'A' | null;
};

export type DeleteEmployeeAndContributionRequest = {
  ertnId: number | null;
  employeeId: number;
};

export type BatchHistoryResponse = AppStandardResponseWithoutNesting<BatchHistoryResponseItem[]>;

const ertnApi = baseAppApi.injectEndpoints({
  endpoints: (build) => ({
    getErtnByYearByStatus: build.mutation<
      GetErtnByYearByStatusResponseTransformed,
      { year: number; pageNum: number }
    >({
      async queryFn({ year, pageNum }, _queryApi, _extraOptions, fetchWithBQ) {
        const boardId = getBoardId();

        const getErtnByYearResponse = (await fetchWithBQ({
          url: `v1/ertn/employers/${boardId}/ertns`,
          params: { schoolYear: year, pageNum, pageSize: ERTN_PAGE_SIZE },
          method: 'GET',
          headers: COMMON_HEADERS,
        })) as QueryReturnValue<
          AppStandardResponse<GetERTNByYearItem>,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (getErtnByYearResponse.error) {
          return {
            error: getErtnByYearResponse.error,
          };
        }

        const range = getRangeFromHeader(getErtnByYearResponse.meta);
        const ertnByYearResponse = getErtnByYearResponse.data;
        if (ertnByYearResponse.data.ertns.length === 0) {
          return {
            data: {
              ertns: [] as GetErtnByYearByStatusResponseTransformed['ertns'],
              range,
            },
          };
        }

        return {
          data: {
            ertns: ertnByYearResponse.data.ertns.map((ertn) => ({
              ...ertn,
              status: getComputedBatchStatus(ertn),
              insertionTimestamp: ertn.insertionTimestamp,
            })),
            range,
          },
        };
      },
    }),
    getErtnByYear: build.mutation<GetErtnBatchResponse, { year: number; pageNum?: number }>({
      query({ year, pageNum = 1 }) {
        const boardId = getBoardId();
        return {
          url: `v1/ertn/employers/${boardId}/ertns`,
          params: { schoolYear: year, pageNum, pageSize: ERTN_PAGE_SIZE },
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: AppStandardResponse<GetERTNByYearItem>, meta) {
        const range = getRangeFromHeader(meta);
        return {
          ertns: response.data.ertns.map((ertn) => ({
            ...ertn,
            status: ertn.status === null ? '' : ertn.status,
            insertionTimestamp: ertn.insertionTimestamp,
          })),
          range,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['FETCH_FAILED']);
      },
    }),
    getIfActiveBatchPresent: build.query<boolean, void>({
      query() {
        const boardId = getBoardId();
        return {
          url: `v1/ertn/employers/${boardId}/ertns`,
          method: 'GET',
          params: {
            active: 1,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: AppStandardResponse<GetERTNByYearItem>) {
        const ertns = response.data.ertns;
        return ertns.length > 0;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['FETCH_FAILED']);
      },
      providesTags: ['ERTNS'],
    }),
    getErtnEmployees: build.query<GetErtnEmployeesTransformed[], { ertnId: number }>({
      query({ ertnId }) {
        const boardId = getBoardId();
        return {
          url: `v1/ertn/employers/${boardId}/employees/ertns/${ertnId}`,
          params: { sort: 'asc' },
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: AppStandardResponseWithoutNesting<GetErtnEmployees[]>) {
        return response.data.map(
          ({
            employeeId,
            firstName,
            lastName,
            sin,
            terminationStatus,
            terminationDate,
            contributions,
            bypassIndicator,
          }) => ({
            employeeId,
            firstName,
            lastName,
            sin,
            terminationStatus,
            terminationDate,
            bypassIndicator,
            records: contributions.length,
            hasErrors: contributions.some(
              (contribution) => contribution.preSubmitError || contribution.hasError,
            ),
          }),
        );
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['FETCH_EMPLOYEES_FAILED']);
      },
      providesTags: ['ERTN_EMPLOYEES'],
    }),
    createErtnBatch: build.mutation<
      AppStandardResponseWithoutNesting<{ payload: AddErtnBatchPayload; ertnId: number }>,
      AddErtnBatchPayload
    >({
      query(body) {
        const boardId = getBoardId();
        return {
          url: `/v1/ertn/employers/${boardId}/ertns`,
          method: 'POST',
          headers: COMMON_HEADERS,
          body,
        };
      },
      transformResponse(
        response: AppStandardResponseWithoutNesting<{ ertnId: number }>,
        meta,
        arg,
      ) {
        return {
          ...response,
          data: {
            ...response.data,
            payload: arg,
          },
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['CREATE_FAILED']);
      },
      invalidatesTags: ['ERTNS'],
    }),
    updateErtn: build.mutation<void, UpdateErtnBatchPayload>({
      query(payload) {
        const boardId = getBoardId();
        return {
          url: `/v1/ertn/employers/${boardId}/ertns/${payload.ertnId}`,
          method: 'PUT',
          headers: COMMON_HEADERS,
          body: payload,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['UPDATE_FAILED']);
      },
    }),
    addToERTNForATRFUser: build.mutation<
      void,
      Pick<CopyEmployeeRequest, 'employeeId' | 'ertnId'> & { notes: string }
    >({
      query({ employeeId, ertnId, notes }) {
        const boardId = getBoardId();
        const userPortalRole = getUserPortalRole();
        const isATRFUser = userPortalRole === PORTAL_ROLES.ATRF;

        if (!isATRFUser || !notes) {
          throw new Error('Invalid request parameters');
        }

        return {
          url: `v1/ertn/employers/${boardId}/employees/ertns/${ertnId}`,
          method: 'POST',
          headers: COMMON_HEADERS,
          body: { employeeId, notes },
        };
      },
    }),
    validateAndAddToErtn: build.mutation<unknown, CopyEmployeeRequest>({
      async queryFn(
        { employeeId, ertnId, employeeIdsToValidate },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const boardId = getBoardId();

        const validateResponse = (await fetchWithBQ({
          url: `/v1/contribution-calculator/employers/${boardId}/validateemployee`,
          method: 'POST',
          body: {
            employeeIds: employeeIdsToValidate,
          },
          headers: COMMON_HEADERS,
        })) as QueryReturnValue<
          ValidateUnsubmittedResponse,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (validateResponse.error) {
          return {
            error: validateResponse.error,
          };
        } else if (validateResponse.data) {
          const warningsExcluded = excludeWarningsFromValidationErrors(validateResponse.data);
          const transformedResponse = transformValidateServiceResponse(warningsExcluded);

          if (isValidValidateUnsubmittedResponse(transformedResponse)) {
            const errorCount = transformedResponse.errors;
            if (errorCount === 0) {
              const addToErtnResponse = await fetchWithBQ({
                url: `v1/ertn/employers/${boardId}/employees/ertns/${ertnId}`,
                method: 'POST',
                headers: COMMON_HEADERS,
                body: { employeeId },
              });

              if (addToErtnResponse.error) {
                return {
                  error: addToErtnResponse.error,
                };
              } else {
                return {
                  data: addToErtnResponse.data,
                };
              }
            } else {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: tokens['VALIDATION_FAILED'],
                },
              };
            }
          }
        }

        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: tokens['VALIDATION_ERROR'],
          },
        };
      },
    }),
    deleteErtnBatch: build.mutation<void, { ertnId: number }>({
      query({ ertnId }) {
        const boardId = getBoardId();
        return {
          url: `v1/ertn/employers/${boardId}/ertns/${ertnId}`,
          method: 'DELETE',
          headers: COMMON_HEADERS,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['DELETE_ERTN_FAILED']);
      },
      invalidatesTags: ['ERTN_EMPLOYEES'],
    }),
    deleteEmployeeAndContribution: build.mutation<void, DeleteEmployeeAndContributionRequest>({
      query({ employeeId, ertnId }) {
        const boardId = getBoardId();
        return {
          url: `v1/ertn/employers/${boardId}/employees/ertns/${ertnId}`,
          method: 'DELETE',
          headers: COMMON_HEADERS,
          body: { employeeId },
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['DELETE_EMPLOYEE_AND_CONTRIBUTION_FAILED']);
      },
      invalidatesTags: ['ERTN_EMPLOYEES'],
    }),
    getBatchHistory: build.mutation<BatchHistoryResponse, { sin: string }>({
      query(sin) {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/employees/batchhistory`,
          method: 'POST',
          headers: COMMON_HEADERS,
          body: sin,
        };
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['FETCH_BATCH_HISTORY_FAILED']);
      },
      invalidatesTags: ['ERTN_EMPLOYEES'],
    }),
  }),
});

export { ertnApi };
